import React, { useState, useEffect } from 'react';
import { X, Check, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const acceptAll = () => {
    setSettings({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptSelected = () => {
    saveSettings(settings);
  };

  const decline = () => {
    const minimalSettings: CookieSettings = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveSettings(minimalSettings);
  };

  const saveSettings = (selectedSettings: CookieSettings) => {
    localStorage.setItem('cookieConsent', JSON.stringify(selectedSettings));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
            </h2>
            <p className="text-sm text-gray-600 mb-4 md:mb-0">
              {language === 'de' 
                ? 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Gemäß der DSGVO benötigen wir Ihre Einwilligung für nicht-essentielle Cookies.'
                : 'We use cookies to provide you with the best possible experience on our website. According to GDPR, we need your consent for non-essential cookies.'}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 md:ml-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-blue-900 hover:text-blue-700 font-medium text-sm"
            >
              {language === 'de' ? 'Cookie-Details' : 'Cookie Details'}
            </button>
            <button
              onClick={decline}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <XCircle className="h-4 w-4 mr-2" />
              {language === 'de' ? 'Ablehnen' : 'Decline'}
            </button>
            <button
              onClick={acceptSelected}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-900 hover:bg-blue-800"
            >
              <Check className="h-4 w-4 mr-2" />
              {language === 'de' ? 'Auswahl bestätigen' : 'Accept Selected'}
            </button>
            <button
              onClick={acceptAll}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              {language === 'de' ? 'Alle akzeptieren' : 'Accept All'}
            </button>
          </div>
        </div>

        {showDetails && (
          <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">
                  {language === 'de' ? 'Notwendige Cookies' : 'Necessary Cookies'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'de' 
                    ? 'Essentiell für die Grundfunktionen der Website'
                    : 'Essential for basic website functionality'}
                </p>
              </div>
              <div className="bg-gray-100 px-3 py-1 rounded text-sm">
                {language === 'de' ? 'Erforderlich' : 'Required'}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">
                  {language === 'de' ? 'Analyse-Cookies' : 'Analytics Cookies'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'de'
                    ? 'Helfen uns, die Websitenutzung zu verstehen'
                    : 'Help us understand website usage'}
                </p>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.analytics}
                  onChange={(e) => setSettings({...settings, analytics: e.target.checked})}
                  className="form-checkbox h-4 w-4 text-blue-900"
                />
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">
                  {language === 'de' ? 'Marketing-Cookies' : 'Marketing Cookies'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'de'
                    ? 'Für personalisierte Werbung und Inhalte'
                    : 'For personalized ads and content'}
                </p>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.marketing}
                  onChange={(e) => setSettings({...settings, marketing: e.target.checked})}
                  className="form-checkbox h-4 w-4 text-blue-900"
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}